import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { SupportTicket, TicketStatus, TicketPriority } from '../../types/support-ticket';
import { format } from 'date-fns';
import { Clock, Tag, User, Info, AlertCircle } from 'lucide-react';

interface TicketPropertiesProps {
  ticket: SupportTicket;
  onUpdateStatus: (status: TicketStatus) => void;
  onUpdatePriority: (priority: TicketPriority) => void;
}

const TicketProperties: React.FC<TicketPropertiesProps> = ({
  ticket,
  onUpdateStatus,
  onUpdatePriority
}) => {
  const getDueTime = () => {
    if (!ticket.due_date) return null;
    
    const dueDate = new Date(ticket.due_date);
    const now = new Date();
    
    if (dueDate < now) {
      return {
        text: 'Vencido',
        className: 'text-red-600 font-extrabold',
        isBreached: true
      };
    }
    
    // Calcular horas restantes
    const hoursLeft = Math.floor((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (hoursLeft < 2) {
      return {
        text: `Vence em ${hoursLeft}h`,
        className: 'text-orange-600 font-extrabold',
        isBreached: false
      };
    }
    
    return {
      text: `Vence em ${hoursLeft}h`,
      className: 'text-green-600 font-extrabold',
      isBreached: false
    };
  };

  const getStatusBadgeVariant = (status: TicketStatus) => {
    switch (status) {
      case 'new':
        return 'default';
      case 'open':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'on_hold':
        return 'outline';
      case 'solved':
        return 'success';
      case 'closed':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getPriorityBadgeVariant = (priority: TicketPriority) => {
    switch (priority) {
      case 'low':
        return 'outline';
      case 'medium':
        return 'secondary';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const dueTimeInfo = getDueTime();

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Propriedades do Ticket</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ID do Ticket */}
        <div>
          <p className="text-sm text-muted-foreground mb-1">ID do Ticket</p>
          <p className="font-extrabold">#{ticket.id.split('-')[1]}</p>
        </div>

        {/* Status */}
        <div>
          <Label htmlFor="status" className="text-sm text-muted-foreground">Status</Label>
          <Select
            defaultValue={ticket.status}
            onValueChange={(value) => onUpdateStatus(value as TicketStatus)}
          >
            <SelectTrigger id="status" className="mt-1">
              <SelectValue>
                <div className="flex items-center">
                  <Badge variant={getStatusBadgeVariant(ticket.status)} className="mr-2">
                    {ticket.status.replace('_', ' ')}
                  </Badge>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Nova</SelectItem>
              <SelectItem value="open">Aberto</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="on_hold">Em espera</SelectItem>
              <SelectItem value="solved">Resolvido</SelectItem>
              <SelectItem value="closed">Fechado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Prioridade */}
        <div>
          <Label htmlFor="priority" className="text-sm text-muted-foreground">Prioridade</Label>
          <Select
            defaultValue={ticket.priority}
            onValueChange={(value) => onUpdatePriority(value as TicketPriority)}
          >
            <SelectTrigger id="priority" className="mt-1">
              <SelectValue>
                <div className="flex items-center">
                  <Badge variant={getPriorityBadgeVariant(ticket.priority)} className="mr-2">
                    {ticket.priority}
                  </Badge>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Baixa</SelectItem>
              <SelectItem value="medium">Média</SelectItem>
              <SelectItem value="high">Alta</SelectItem>
              <SelectItem value="urgent">Urgente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tempo SLA */}
        <div>
          <p className="text-sm text-muted-foreground mb-1">Prazo SLA</p>
          {dueTimeInfo ? (
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1.5" />
              <span className={dueTimeInfo.className}>{dueTimeInfo.text}</span>
              {dueTimeInfo.isBreached && (
                <Badge variant="destructive" className="ml-2">SLA violado</Badge>
              )}
            </div>
          ) : (
            <p>Não definido</p>
          )}
        </div>

        {/* Agente responsável */}
        <div>
          <Label htmlFor="agent" className="text-sm text-muted-foreground">Agente responsável</Label>
          <div className="flex items-center gap-2 mt-1">
            <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              A
            </div>
            <p>{ticket.agent_id ? 'John Smith' : 'Não atribuído'}</p>
            <Button variant="outline" size="sm" className="ml-auto h-7 px-2 py-0">
              <User className="h-3.5 w-3.5 mr-1" />
              Atribuir
            </Button>
          </div>
        </div>

        {/* Etiquetas */}
        <div>
          <Label className="text-sm text-muted-foreground">Etiquetas</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            <Badge variant="outline" className="bg-blue-50">suporte</Badge>
            <Badge variant="outline" className="bg-green-50">cliente</Badge>
            <Button variant="outline" size="sm" className="h-6 px-2 py-0">
              <Tag className="h-3.5 w-3.5 mr-1" />
              Adicionar
            </Button>
          </div>
        </div>

        {/* Criado/Atualizado */}
        <div className="border-t pt-3 mt-3">
          <div className="flex items-center text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mr-1" />
            <span>Criado: {format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm')}</span>
          </div>
          <div className="flex items-center text-xs text-muted-foreground mt-1">
            <Info className="h-3.5 w-3.5 mr-1" />
            <span>Atualizado: {format(new Date(ticket.updated_at), 'dd/MM/yyyy HH:mm')}</span>
          </div>
        </div>

        {/* Botões de ação */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button variant="outline" size="sm" className="w-full">
            Mesclar
          </Button>
          <Button variant="destructive" size="sm" className="w-full">
            Encerrar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TicketProperties; 